import {
  useActionMenuTokenInputData,
  useGetOnSwapTokenAmount,
} from '#/components/actionmenu/token-input/use-action-menu-token-input-data';
import {MaxButton} from '#/components/buttons/max-button';
import {SwapTokenButton} from '#/components/buttons/swap-token-button';
import {TokenType} from '#/repository/types';
import {tokenAmountToInputString} from '#/utils/token-string-utils';
import React, {FunctionComponent, ReactNode} from 'react';

type ActionMenuTokenInputProps = {
  tokenInputData: ReturnType<typeof useActionMenuTokenInputData>;
  maxButtonLandAmount?: number;
  viewOnly?: boolean;
};

export const ActionMenuTokenInput: FunctionComponent<ActionMenuTokenInputProps> = ({
  tokenInputData,
  maxButtonLandAmount,
  viewOnly = false,
}) => {
  const {selectedTokenConstants, unselectedTokenConstants, amount, tokenInput, convertedAmount} = tokenInputData;
  const inputValid = !tokenInput.validationMessage;
  const convertSecondaryAmount = tokenInput.input !== '' && amount > 0 && inputValid;

  return (
    <Container viewOnly={viewOnly}>
      <PrimaryTokenInput
        input={tokenInput.input}
        decimalsCount={selectedTokenConstants.decimals}
        currencySymbol={selectedTokenConstants.currencySymbol}
        onChange={tokenInput.onInputChange}
        onBlur={tokenInput.onInputBlur}
        viewOnly={viewOnly}
        inputValid={inputValid}
      />

      <SecondayTokenDisplay
        value={unselectedTokenConstants.display(convertSecondaryAmount ? convertedAmount : 0)}
        valueEnabled={convertSecondaryAmount}
        currencySymbol={unselectedTokenConstants.currencySymbol}
      />

      <ButtonContainer>
        {!!maxButtonLandAmount && (
          <ActionMenuTokenInputMaxButton tokenInputData={tokenInputData} maxButtonLandAmount={maxButtonLandAmount} />
        )}
        <ActionMenuTokenInputSwapTokenButton tokenInputData={tokenInputData} />
      </ButtonContainer>
    </Container>
  );
};

const Container: FunctionComponent<Pick<ActionMenuTokenInputProps, 'viewOnly'> & {children: ReactNode}> = ({
  children,
  viewOnly,
}) => {
  return (
    <div
      className={
        'w-full h-full grid auto-rows-min grid-cols-[1fr,auto] gap-x-6 gap-y-3 overflow-x-visible data-[viewonly=true]:view-only'
      }
      data-viewonly={viewOnly}
    >
      {children}
    </div>
  );
};

const PrimaryTokenInput: FunctionComponent<{
  input: string;
  decimalsCount: number;
  currencySymbol: string;
  onChange: ReturnType<typeof useActionMenuTokenInputData>['tokenInput']['onInputChange'];
  onBlur: ReturnType<typeof useActionMenuTokenInputData>['tokenInput']['onInputBlur'];
  viewOnly: boolean;
  inputValid: boolean;
}> = ({input, decimalsCount, currencySymbol, onChange, onBlur, viewOnly, inputValid}) => {
  const step = Math.pow(10, -decimalsCount);

  return (
    <div
      className='row-start-1 row-span-1 col-start-1 col-span-2 bg-graybluedark rounded-2xl p-4 border border-graybluedark focus-within:border-deepblue focus-within:data-[valid=false]:border-downred'
      data-valid={inputValid}
    >
      <input
        className='w-full bg-transparent block text-white text-5xl appearance-none outline-none'
        onChange={onChange}
        onBlur={onBlur}
        placeholder='0'
        min='0'
        step={step}
        value={input}
        type='number'
        disabled={viewOnly}
      />
      <span className='text-blue-600 text-4xl'>{currencySymbol}</span>
    </div>
  );
};

const SecondayTokenDisplay: FunctionComponent<{
  value: string;
  valueEnabled: boolean;
  currencySymbol: string;
}> = ({value, valueEnabled, currencySymbol}) => {
  return (
    <span
      className='row-start-2 row-span-1 col-start-1 col-span-1 flex-1 ml-2 flex flex-col text-gray-400 data-[valueenabled=true]:text-white'
      data-valueenabled={valueEnabled}
    >
      <span>{value}</span>
      <span>{currencySymbol}</span>
    </span>
  );
};

const ButtonContainer: FunctionComponent<{children: ReactNode}> = ({children}) => {
  return <div className={'row-start-2 row-span-1 col-start-2 col-span-1 flex gap-2 py-1'}>{children}</div>;
};

const ActionMenuTokenInputMaxButton: FunctionComponent<{
  tokenInputData: ReturnType<typeof useActionMenuTokenInputData>;
  maxButtonLandAmount?: number;
}> = ({
  tokenInputData: {
    tokenKeySelection,
    unselectedTokenConstants,
    selectedTokenConstants,
    tokenInput,
    tokenAmounts: {landTokenAmount},
  },
  maxButtonLandAmount,
}) => {
  const onSetMaxAmount = () => {
    if (tokenKeySelection.selected === TokenType.STABLE) {
      tokenKeySelection.toggle();
      tokenInput.onChange(tokenAmountToInputString(maxButtonLandAmount, unselectedTokenConstants.decimals, false));
    } else {
      tokenInput.onChange(tokenAmountToInputString(maxButtonLandAmount, selectedTokenConstants.decimals, false));
    }
  };

  const onClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    onSetMaxAmount();
  };

  return <MaxButton disabled={maxButtonLandAmount === landTokenAmount} onClick={onClick} />;
};

const ActionMenuTokenInputSwapTokenButton: FunctionComponent<{
  tokenInputData: ReturnType<typeof useActionMenuTokenInputData>;
}> = ({
  tokenInputData: {tokenKeySelection, selectedTokenConstants, unselectedTokenConstants, tokenInput, convertedAmount},
}) => {
  const getOnSwapTokenAmount = useGetOnSwapTokenAmount();

  const onSwapToken = () => {
    tokenKeySelection.toggle();
    const adjustedAmount = getOnSwapTokenAmount(tokenKeySelection.unselected, convertedAmount);
    tokenInput.onChange(tokenAmountToInputString(adjustedAmount, selectedTokenConstants.decimals, false));
  };

  const onClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    onSwapToken();
  };

  return <SwapTokenButton onClick={onClick} />;
};
